// 2) Third-party libraries
import { get } from "lodash-es";
import {
    all,
    call,
    fork,
    put,
    select,
    takeLatest
} from "redux-saga/effects";

// 3) Local imports
// 3.1 Constants
import { API_NAME } from "../../config/config";
import { DETAILS_TAB } from '../../config/tableAndPageConstants';

// 3.3 Project-specific modules � components/types/other
import client, { delApi, post } from "src/api";
import { _queries as queries } from "../../graphql/queries.graphql";
import { WorkflowConfiguration } from "./types";
import { CompanyUserRole } from "../companies/types";

// 3.4 Utility functions / types
import { DynamicObject } from "../../utils/commonInterfaces";
import {
    removeAppliedFiltersForApiRequest
} from '../../utils/commonFunctions';

// 3.5 Redux actions / store
import { getCompanyWorkflowOptionsRequestAction, getSelectedUserCompany } from "../companies/companySlice";
import { clearWorkflowContentConfiguration } from "../contents/contentSlice";
import {
    deleteWorkflowsRequestAction,
    getWorkflowConfigurationsErrorAction,
    getWorkflowConfigurationsRequestAction,
    getWorkflowConfigurationsSuccessAction,
    getWorkflowDefinitionErrorAction,
    getWorkflowDefinitionRequestAction,
    getWorkflowDefinitionSuccessAction,
    saveWorkflowDefinitionConfigurationRequestAction,
    setWorkflowSelectedIdRequestAction,
    setWorkflowSelectedIdSuccessAction,
    updateWorkflowRequestAction,
    getWorkflowChangesSuccessAction,
    getWorkflowChangesErrorAction,
    getWorkflowChangesRequestAction
} from "./workflowSlice";

/**
 * Function called for getting the workflow step options for task editing.
 * @param param0
 */
function* handleGetWorkflowDefinitionRequest({ payload: sagaPayload }: any) {
    const errorMessage = 'Error fetching workflow definitions. Please try again later.';

    try {
        const { WorkflowId } = sagaPayload;

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_WORKFLOW_DEFINITION,
                variables: {
                    WorkflowId
                }
            }
        );

        const workflowDefinition = get(res.data, 'GetWorkflowDefinition');

        if (workflowDefinition) {
            yield put(getWorkflowDefinitionSuccessAction(workflowDefinition));
        }
        else {
            yield put(getWorkflowDefinitionErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured: ', err);
        }

        yield put(getWorkflowDefinitionErrorAction([errorMessage]));
    }
}

/**
 * Function called for saving the workflow definition - talking to the API.
 */
function* handleSaveWorkflowDefinitionRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/workflow/definition/update',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
        // calls for queries if needed
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleUpdateWorkflowRequest({ payload: sagaPayload }: any) {
    const { payload, isCreateNew, callback } = sagaPayload;
    try {
        const apiPath = isCreateNew ? '/workflow/update' : '/workflow/definition/steps/update';

        yield call(post, {
            apiName: API_NAME, 
            path: apiPath, 
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
            yield put(getCompanyWorkflowOptionsRequestAction());
            yield put(clearWorkflowContentConfiguration());
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for getting the workflow step options for task editing.
 * @param param0
 */
function* handleWorkflowConfigurationRequest({ payload }: any) {
    const errorMessage = 'Error fetching workflow configuration. Please try again later.';
    try {
        const { callback, ...requestPayload } = payload;

        const selectedUserCompany: CompanyUserRole | undefined = yield select(getSelectedUserCompany);
        const usingCustomerWorkflow = get(selectedUserCompany, ['Company', 'UsingCustomerWorkflow']);

        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_WORKFLOW_CONFIGURATION_FOR_COMPANY
            }
        );

        const workflowConfiguration = get(res.data, 'GetWorkflowConfigurationForCompany');
        const workflows: DynamicObject[] = [];

        if (workflowConfiguration) {
            if (usingCustomerWorkflow) {
                workflows.push(...(workflowConfiguration.Customers || []));
            }
            else {
                workflows.push(...(workflowConfiguration.Invoices || []));
            }

            const result = workflows as WorkflowConfiguration[];

            yield put(getWorkflowConfigurationsSuccessAction({ data: result }));

            if (typeof callback === 'function') {
                callback(result);
            }
        } else {
            yield put(getWorkflowConfigurationsErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
        yield put(getWorkflowConfigurationsErrorAction([errorMessage]));
    }
}

function pickMessages(data: any): string[] {
    if (!data) return ['An unknown error occurred.'];
    if (Array.isArray(data.Messages)) return data.Messages;
    if (typeof data.Message === 'string') return [data.Message];
    if (typeof data.message === 'string') return [data.message];
    if (typeof data === 'string') return [data];
    return ['An error occurred.'];
}

/**
 * Function that connects to delete workflows api.
 */
function* handleDeleteWorkflowsRequest({ payload: sagaPayload }: any) {
    const { WorkflowIds, callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const apiResponse: DynamicObject = yield call(delApi, 
            '/workflow/delete', 
            {
                WorkflowIds: WorkflowIds
            }
        );

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: get(apiResponse, 'Messages')
            };

            callback(response);
            yield put(getCompanyWorkflowOptionsRequestAction());
        }
    } catch (err: any) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            }
            else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function that sets the selectedId upon clicking the row item.
 * @param param0
 */
function* handleSetWorkflowSelectedIdRequest({ payload }: any) {
    const { workflowId, callback } = payload;
    yield put(setWorkflowSelectedIdSuccessAction(workflowId));
    callback();
}

/**
 * Function for fetching the workflowChanges list of a given workflow.
 * @param param0
 */
function* handleGetWorkflowChangesRequest({ payload }: any) {
    const errorMessage = `Error fetching workflow changes list. Please try again later.`;
    try {
        const { filters, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CHANGE_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.WORKFLOW_CHANGES_TIMELINE.pageSize,
                }
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.WORKFLOW_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getWorkflowChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getWorkflowChangesErrorAction([errorMessage]));
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetWorkflowDefinitionRequest() {
    yield takeLatest(
        getWorkflowDefinitionRequestAction.type,
        handleGetWorkflowDefinitionRequest
    );
}

function* watchSaveWorkflowDefinitionRequest() {
    yield takeLatest(
        saveWorkflowDefinitionConfigurationRequestAction.type,
        handleSaveWorkflowDefinitionRequest
    );
}

function* watchUpdateWorkflowRequest() {
    yield takeLatest(
        updateWorkflowRequestAction.type,
        handleUpdateWorkflowRequest
    );
}

function* watchWorkflowConfigurationRequest() {
    yield takeLatest(
        getWorkflowConfigurationsRequestAction.type,
        handleWorkflowConfigurationRequest
    );
}

function* watchSetWorkflowSelectedIdRequest() {
    yield takeLatest(
        setWorkflowSelectedIdRequestAction.type,
        handleSetWorkflowSelectedIdRequest
    );
}

function* watchDeleteWorkflowsRequest() {
    yield takeLatest(
        deleteWorkflowsRequestAction.type,
        handleDeleteWorkflowsRequest
    );
}

function* watchGetWorkflowChangesRequest() {
    yield takeLatest(
        getWorkflowChangesRequestAction.type,
        handleGetWorkflowChangesRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* workflowsSaga() {
    yield all([
        fork(watchGetWorkflowDefinitionRequest),
        fork(watchSaveWorkflowDefinitionRequest),
        fork(watchUpdateWorkflowRequest),
        fork(watchWorkflowConfigurationRequest),
        fork(watchSetWorkflowSelectedIdRequest),
        fork(watchDeleteWorkflowsRequest),
        fork(watchGetWorkflowChangesRequest)
    ]);
}

export default workflowsSaga;
